import { Order, ShippingCompany } from "./types";
import { normalizeTimestamp } from "../../utils/dateFormatter";

// Shipping companies configuration
export const SHIPPING_COMPANIES: ShippingCompany[] = [
    // Greece post offices
    {
        id: "easymail",
        name: "EasyMail",
        countries: ["gr", "GR"],
        status: "connected",
        apiEndpoint: "/api/shipping/easymail",
        lastUpdate: new Date().toISOString(),
    },
    {
        id: "acs",
        name: "ACS",
        countries: ["gr", "GR"],
        status: "connected",
        apiEndpoint: "/api/shipping/acs",
    },
    {
        id: "postx",
        name: "Post X",
        countries: ["gr", "GR"],
        status: "disconnected",
        apiEndpoint: "/api/shipping/postx",
    },
    // Bulgaria post offices
    {
        id: "econt",
        name: "Econt",
        countries: ["bg", "BG"],
        status: "disconnected",
        apiEndpoint: "/api/shipping/econt",
    },
    // Macedonia post offices
    {
        id: "mex",
        name: "MEX",
        countries: ["mk", "MK"],
        status: "paused",
        apiEndpoint: "/api/shipping/mex",
    },
    // Serbia post offices
    {
        id: "bex",
        name: "Bex Express",
        countries: ["rs", "RS"],
        status: "connected",
        apiEndpoint: "/api/shipping/bex",
        lastUpdate: new Date().toISOString(),
    },
];

// Country flag mapping
export const getCountryFlag = (countryCode: string): string => {
    const flags: { [key: string]: string } = {
        mk: "🇲🇰",
        rs: "🇷🇸",
        bg: "🇧🇬",
        gr: "🇬🇷",
        al: "🇦🇱",
        ks: "🇽🇰",
        me: "🇲🇪",
        ba: "🇧🇦",
        hr: "🇭🇷",
        si: "🇸🇮",
    };
    return flags[countryCode?.toLowerCase()] || "🏳️";
};

// Get shipping company for country
export const getShippingCompanyForCountry = (
    country: string
): ShippingCompany | null => {
    return (
        SHIPPING_COMPANIES.find(
            (company) =>
                company.countries.includes(country?.toLowerCase()) ||
                company.countries.includes(country?.toUpperCase())
        ) || null
    );
};

// Function to format phone numbers with country codes
export const formatPhoneNumber = (phone: string, removeSpaces: boolean = false): string => {
    if (!phone) return "N/A";

    // Remove all spaces and non-digit characters
    const digits = phone.replace(/\s/g, "").replace(/\D/g, "");
    
    // If removeSpaces is true (for Serbia), return raw number without formatting
    if (removeSpaces) {
        return digits;
    }

    // Format based on length and patterns
    if (digits.length === 9) {
        // Serbian format: 123456789 -> +381 12 345 6789
        if (digits.startsWith("6")) {
            return `+381 ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(
                5
            )}`;
        }
        // Macedonian format: 123456789 -> +389 12 345 6789
        if (digits.startsWith("7")) {
            return `+389 ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(
                5
            )}`;
        }
        // Bulgarian format: 123456789 -> +359 12 345 6789
        if (digits.startsWith("8")) {
            return `+359 ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(
                5
            )}`;
        }
    }

    if (digits.length === 10) {
        // International format: 1234567890 -> +1 234 567 890
        return `+${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(
            4,
            7
        )} ${digits.slice(7)}`;
    }

    if (digits.length === 11) {
        // International format: 12345678901 -> +12 345 678 901
        return `+${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(
            5,
            8
        )} ${digits.slice(8)}`;
    }

    // Default formatting for other lengths
    if (digits.length > 0) {
        return `+${digits}`;
    }

    return phone; // Return original if no digits found
};

// Function to get AlterCPA status based on order status
export const getAlterCPAStatus = (order: Order): { name: string; color: string } => {
    // Special case: Status 5 with stage 235 (Cancelled but approved)
    if (order.status === 5 && (order as any).stage === 235) {
        return { name: "Cancelled: Other", color: "bg-orange-100 text-orange-800" };
    }

    // If order has tracking number, show BEX status
    if (order.trackingId && order.shippingCompany === "bex" && order.bexStatus) {
        const mappedStatus = mapShippingStatusToAlterCPA(order.bexStatus.code);
        return { name: mappedStatus.name, color: mappedStatus.color };
    }

    // If no tracking, show AlterCPA status progression
    switch (order.status) {
        case 5:
            return { name: "CANCELLED", color: "bg-red-100 text-red-800" };
        case 6:
            return { name: "PACKAGING", color: "bg-yellow-100 text-yellow-800" };
        case 7:
            return { name: "SENDING", color: "bg-blue-100 text-blue-800" };
        case 8:
            return { name: "TRANSFER", color: "bg-purple-100 text-purple-800" };
        case 9:
            return { name: "ARRIVED", color: "bg-indigo-100 text-indigo-800" };
        case 10:
            return { name: "COMPLETED", color: "bg-green-100 text-green-800" };
        case 11:
            return { name: "RETURNED", color: "bg-red-100 text-red-800" };
        default:
            return { name: "PACKAGING", color: "bg-yellow-100 text-yellow-800" };
    }
};

// Post offices for Greece
export const GREECE_POST_OFFICES = [
    {
        id: "easymail",
        name: "EasyMail",
        status: "connected",
        description: "EasyMail shipping service for Greece"
    },
    {
        id: "acs",
        name: "ACS",
        status: "connected",
        description: "ACS shipping service for Greece"
    },
    {
        id: "postx",
        name: "Post X",
        status: "disconnected",
        description: "Post X shipping service for Greece"
    }
];

// Get available post offices for a country
export const getPostOfficesForCountry = (countryCode: string) => {
    const country = countryCode.toLowerCase();

    if (country === 'gr' || country === 'greece') {
        return GREECE_POST_OFFICES;
    }

    // For other countries, return the shipping company as post office
    return SHIPPING_COMPANIES.filter(company =>
        company.countries.some(c => c.toLowerCase() === country)
    ).map(company => ({
        id: company.id,
        name: company.name,
        status: company.status,
        description: `${company.name} shipping service`
    }));
};

// Check if order needs post office selection
export const needsPostOfficeSelection = (order: Order): boolean => {
    const country = order.country?.toLowerCase();

    // Greece only uses ACS - skip post office selection step
    if (country === 'gr' || country === 'greece') {
        return false;
    }

    return false;
};

// Function to get Post Office status
export const getPostOfficeStatus = (order: Order): { name: string; color: string } => {
    // If order has tracking, show shipping company status
    if (order.trackingId && order.shippingCompany) {
        // BEX status handling
        if (order.shippingCompany === "bex" && order.bexStatus) {
            switch (order.bexStatus.code) {
                case 1:
                    return { name: "BEX: REGISTERED", color: "bg-blue-100 text-blue-800" };
                case 11:
                    return { name: "BEX: IN TRANSIT", color: "bg-yellow-100 text-yellow-800" };
                case 21:
                    return { name: "BEX: DELIVERED", color: "bg-green-100 text-green-800" };
                case 25:
                case 30:
                    return { name: "BEX: RETURNED", color: "bg-red-100 text-red-800" };
                case -51:
                    return { name: "BEX: CUSTOMS", color: "bg-orange-100 text-orange-800" };
                case -21:
                    return { name: "BEX: ADDRESS CONFIRMATION", color: "bg-yellow-100 text-yellow-800" };
                default:
                    return { name: "BEX: REGISTERED", color: "bg-blue-100 text-blue-800" };
            }
        }

        // EasyMail status handling
        if (order.shippingCompany === "easymail" && order.easyMailStatus) {
            switch (order.easyMailStatus.code) {
                case 'CREATED':
                    return { name: "EASYMAIL: CREATED", color: "bg-blue-100 text-blue-800" };
                case 'IN_TRANSIT':
                    return { name: "EASYMAIL: IN TRANSIT", color: "bg-yellow-100 text-yellow-800" };
                case 'OUT_FOR_DELIVERY':
                    return { name: "EASYMAIL: OUT FOR DELIVERY", color: "bg-orange-100 text-orange-800" };
                case 'DELIVERED':
                    return { name: "EASYMAIL: DELIVERED", color: "bg-green-100 text-green-800" };
                case 'CANCELLED':
                    return { name: "EASYMAIL: CANCELLED", color: "bg-red-100 text-red-800" };
                case 'RETURNED':
                    return { name: "EASYMAIL: RETURNED", color: "bg-red-100 text-red-800" };
                case 'EXCEPTION':
                    return { name: "EASYMAIL: EXCEPTION", color: "bg-red-100 text-red-800" };
                default:
                    return { name: "EASYMAIL: CREATED", color: "bg-blue-100 text-blue-800" };
            }
        }

        // Generic shipping company status
        const company = SHIPPING_COMPANIES.find(c => c.id === order.shippingCompany);
        return {
            name: company?.name || order.shippingCompany.toUpperCase(),
            color: "bg-green-100 text-green-800"
        };
    }

    // If post office is selected but not shipped yet
    if (order.selectedPostOffice) {
        const postOffice = GREECE_POST_OFFICES.find(po => po.id === order.selectedPostOffice);
        return {
            name: `Selected: ${postOffice?.name || order.selectedPostOffice}`,
            color: "bg-blue-100 text-blue-800"
        };
    }

    // If needs post office selection
    if (needsPostOfficeSelection(order)) {
        return {
            name: "Select Post Office",
            color: "bg-yellow-100 text-yellow-800"
        };
    }

    // Default for other countries or ready orders
    return { name: "READY", color: "bg-gray-100 text-gray-800" };
};

// Function to check if tracking should be stopped
export const shouldStopTracking = (order: Order): boolean => {
    // Stop tracking if status is packaging (6)
    if (order.status === 6) {
        return true;
    }

    // Stop tracking if status is completed (10) or returned (11)
    if (order.status === 10 || order.status === 11) {
        return true;
    }

    // Stop tracking if order is older than 1 week
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const orderDate = normalizeTimestamp(order.time);
    if (orderDate < oneWeekAgo) {
        return true;
    }

    return false;
};

// Function to map shipping status codes to AlterCPA status codes
export const mapShippingStatusToAlterCPA = (
    shippingStatusCode: number
): { status: number; name: string; color: string } => {
    switch (shippingStatusCode) {
        case -51: // Customs
            return {
                status: shippingStatusCode,
                name: "Customs",
                color: "bg-orange-100 text-orange-800",
            };
        case -21: // For address confirmation
            return {
                status: shippingStatusCode,
                name: "Address Confirmation",
                color: "bg-yellow-100 text-yellow-800",
            };
        case 1: // Registered
            return { status: 7, name: "Sending", color: "bg-blue-100 text-blue-800" };
        case 11: // Picked up
            return {
                status: 8,
                name: "Transfer",
                color: "bg-purple-100 text-purple-800",
            };
        case 21: // Delivered
            return {
                status: 10,
                name: "Completed and paid",
                color: "bg-green-100 text-green-800",
            };
        case 25: // Return to sender
            return {
                status: shippingStatusCode,
                name: "Return to sender",
                color: "bg-orange-100 text-orange-800",
            };
        case 30: // Returned to sender
            return {
                status: 11,
                name: "Returned to sender",
                color: "bg-red-100 text-red-800",
            };
        default:
            return {
                status: shippingStatusCode,
                name: `Status ${shippingStatusCode}`,
                color: "bg-gray-100 text-gray-800",
            };
    }
};

// Get date range for period filter
export const getDateRangeForPeriod = (period: string) => {
    const now = new Date();
    // Use UTC methods to avoid timezone issues
    const currentYear = now.getUTCFullYear();
    const currentMonth = now.getUTCMonth();
    const currentWeek = Math.ceil(now.getUTCDate() / 7);

    console.log(`🔍 [DATE] Current date (UTC):`, now.toISOString());
    console.log(`🔍 [DATE] Current year (UTC):`, currentYear);
    console.log(`🔍 [DATE] Current month (UTC):`, currentMonth);

    switch (period) {
        case "today":
            const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
            const todayEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
            const todayRange = {
                from: Math.floor(todayStart.getTime() / 1000),
                to: Math.floor(todayEnd.getTime() / 1000),
            };
            console.log(`🔍 [DATE] Today range:`, todayRange);
            console.log(`🔍 [DATE] Today start:`, todayStart.toISOString());
            console.log(`🔍 [DATE] Today end:`, todayEnd.toISOString());
            return todayRange;

        case "current_week":
            const currentUTCDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
            const dayOfWeek = currentUTCDate.getUTCDay();
            // Calculate days to Monday (Monday-start week)
            const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday, go back 6 days; otherwise go to Monday
            const weekStart = new Date(currentUTCDate.getTime() + (daysToMonday * 24 * 60 * 60 * 1000));
            // End is tomorrow (to include today)
            const tomorrow = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
            const weekEnd = new Date(tomorrow.getTime() - 1); // End of today (one ms before tomorrow)
            const weekRange = {
                from: Math.floor(weekStart.getTime() / 1000),
                to: Math.floor(weekEnd.getTime() / 1000),
            };
            console.log(`🔍 [DATE] Week range:`, weekRange);
            console.log(`🔍 [DATE] Week start:`, weekStart.toISOString());
            console.log(`🔍 [DATE] Week end:`, weekEnd.toISOString());
            return weekRange;

        case "current_month":
            const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0));
            const monthEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59, 999));
            const monthRange = {
                from: Math.floor(monthStart.getTime() / 1000),
                to: Math.floor(monthEnd.getTime() / 1000),
            };
            console.log(`🔍 [DATE] Month range:`, monthRange);
            console.log(`🔍 [DATE] Month start:`, monthStart.toISOString());
            console.log(`🔍 [DATE] Month end:`, monthEnd.toISOString());
            return monthRange;

        case "current_year":
            const yearStart = new Date(Date.UTC(now.getUTCFullYear(), 0, 1, 0, 0, 0, 0));
            const yearEnd = new Date(Date.UTC(now.getUTCFullYear(), 11, 31, 23, 59, 59, 999));
            const yearRange = {
                from: Math.floor(yearStart.getTime() / 1000),
                to: Math.floor(yearEnd.getTime() / 1000),
            };
            console.log(`🔍 [DATE] Year range:`, yearRange);
            console.log(`🔍 [DATE] Year start:`, yearStart.toISOString());
            console.log(`🔍 [DATE] Year end:`, yearEnd.toISOString());
            return yearRange;

        case "all":
        default:
            console.log(`🔍 [DATE] No date range (all time)`);
            return { from: null, to: null };
    }
};
